import { useEffect, useMemo } from "react"
import "../Dashboard.css"
import "bootstrap-icons/font/bootstrap-icons.css";
import { Formik } from "formik";
import * as Yup from "yup";
import { useDispatch, useSelector } from "react-redux";
import { basicDetails } from "../../../store/campaignBuilderSlice";
import { getCampaignBuilderDefaults } from "../../../utils/campaignBuilderDefaults";
import Input from "./Input";
const PLATFORM_OPTIONS = [
    { value: "instagram", label: "Instagram", icon: "bi-instagram", iconClass: "text-pink-600" },
    { value: "facebook", label: "Facebook", icon: "bi-facebook", iconClass: "text-blue-600" },
    { value: "linkedin", label: "LinkedIn", icon: "bi-linkedin", iconClass: "text-blue-700" },
    { value: "twitter", label: "X / Twitter", icon: "bi-twitter-x", iconClass: "text-slate-800" },
];

const LANGUAGE_OPTIONS = [
    { value: "english", label: "English", badge: "English" },
    { value: "hindi", label: "Hindi", badge: "हिंदी" },
    { value: "bengali", label: "Bengali", badge: "বাংলা" },
];

const validationSchema = Yup.object({
    name: Yup.string().trim().required("Please enter what you're promoting"),
    platform: Yup.string().required("Please select a platform"),
    language: Yup.string().required("Please select a language"),
});

function BasicDetailsFields({
    values,
    errors,
    touched,
    isValid,
    handleChange,
    handleBlur,
    setFieldValue,
    setFieldTouched,
    onValidityChange,
    readOnly = false,
}) {
    const dispatch = useDispatch();

    useEffect(() => {
        onValidityChange?.(readOnly ? false : isValid);
    }, [isValid, onValidityChange, readOnly]);

    useEffect(() => {
        if (readOnly) return;
        dispatch(basicDetails(values));
    }, [values, dispatch, readOnly]);

    return (
        <>
            <section className="space-y-6 md:space-y-8 md:p-6">
                <div>
                    <Input
                        label="What are you promoting?"
                        labelIcon="input-cursor-text"
                        name="name"
                        type="text"
                        wrapperClassName="form-section"
                        placeholder="e.g. Summer sale, new product launch…"
                        value={values.name}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        disabled={readOnly}
                    />
                    {touched.name && errors.name ? (
                        <p className="mt-1 text-xs text-red-600 md:text-sm">{errors.name}</p>
                    ) : null}
                </div>

                <div className="form-section pt-1 md:pt-2">
                    <p className="form-label mb-3 flex items-center gap-2 text-xs font-bold uppercase tracking-[0.12em] text-slate-800 md:mb-4 md:gap-2.5 md:text-sm md:tracking-[0.15em]">
                        <span className="inline-flex h-7 w-7 shrink-0 items-center justify-center rounded-md bg-slate-100 text-slate-700 md:h-8 md:w-8">
                            <i className="bi bi-globe2 text-sm md:text-base" aria-hidden />
                        </span>
                        Choose a platform
                    </p>
                    <div
                        className="grid grid-cols-2 gap-2 sm:grid-cols-4 md:gap-3"
                        role="radiogroup"
                        aria-label="Choose a platform"
                    >
                        {PLATFORM_OPTIONS.map((p) => {
                            const checked = values.platform === p.value;
                            return (
                                <label
                                    key={p.value}
                                    className={`relative flex flex-col items-center gap-2 rounded-xl border px-2 py-3 text-center transition-all duration-200 md:px-3 md:py-4 ${readOnly ? "cursor-not-allowed opacity-60" : "cursor-pointer"} ${checked
                                            ? "border-slate-900 bg-slate-50 ring-2 ring-slate-900/20"
                                            : "border-slate-200 bg-white hover:border-slate-300 hover:bg-slate-50"
                                        }`}
                                >
                                    <input
                                        type="radio"
                                        name="platform"
                                        value={p.value}
                                        checked={checked}
                                        disabled={readOnly}
                                        onChange={() => {
                                            if (readOnly) return;
                                            setFieldValue("platform", p.value);
                                            setFieldTouched("platform", true, false);
                                        }}
                                        onBlur={() => {
                                            if (readOnly) return;
                                            setFieldTouched("platform", true);
                                        }}
                                        className="sr-only"
                                    />
                                    {checked ? (
                                        <span className="absolute -right-1.5 -top-1.5 inline-flex h-6 w-6 items-center justify-center rounded-full border border-slate-900 bg-white text-slate-900 md:h-7 md:w-7">
                                            <i className="bi bi-check-lg text-[10px] md:text-xs" aria-hidden />
                                        </span>
                                    ) : null}
                                    <span
                                        className={`flex h-10 w-10 items-center justify-center rounded-full bg-white/90 md:h-12 md:w-12 ${checked ? "ring-1 ring-slate-300" : ""}`}
                                    >
                                        <i
                                            className={`${p.icon} text-2xl md:text-[1.65rem] ${p.iconClass}`}
                                            aria-hidden
                                        />
                                    </span>
                                    <span className="text-[11px] font-semibold text-slate-800 md:text-xs">
                                        {p.label}
                                    </span>
                                </label>
                            );
                        })}
                    </div>
                    {touched.platform && errors.platform ? (
                        <p className="mt-2 text-xs text-red-600 md:text-sm">{errors.platform}</p>
                    ) : null}
                </div>
            </section>
            <section className="md:mb-8 md:p-6 mt-6 md:mt-0">
                <div className="mb-3 flex items-center gap-2 md:mb-4 md:gap-3">
                    <span className="inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-slate-100 text-slate-700 md:h-9 md:w-9">
                        <i className="bi bi-translate text-base md:text-lg" aria-hidden />
                    </span>
                    <label className="mb-0 text-xs font-bold uppercase tracking-[0.12em] text-slate-800 md:text-sm md:tracking-[0.15em]">Language preference</label>
                </div>

                <div
                    className="grid grid-cols-3 gap-1.5 lg:gap-3"
                    role="radiogroup"
                    aria-label="Language preference"
                >
                    {LANGUAGE_OPTIONS.map((lang) => {
                        const checked = values.language === lang.value;
                        return (
                            <label
                                key={lang.value}
                                className={`relative flex flex-col items-center gap-1 rounded-xl border px-1.5 py-2 text-center transition-all duration-200 lg:gap-2 lg:px-3 lg:py-4 ${readOnly ? "cursor-not-allowed opacity-60" : "cursor-pointer"} ${checked
                                        ? "border-slate-900 bg-slate-50 ring-2 ring-slate-900/20"
                                        : "border-slate-200 bg-white hover:border-slate-300 hover:bg-slate-50"
                                    }`}
                            >
                                <input
                                    type="radio"
                                    name="language"
                                    value={lang.value}
                                    checked={checked}
                                    disabled={readOnly}
                                    aria-label={lang.label}
                                    onChange={() => {
                                        if (readOnly) return;
                                        setFieldValue("language", lang.value);
                                        setFieldTouched("language", true, false);
                                    }}
                                    onBlur={() => {
                                        if (readOnly) return;
                                        setFieldTouched("language", true);
                                    }}
                                    className="sr-only"
                                />
                                {checked ? (
                                    <span className="absolute -right-1.5 -top-1.5 inline-flex h-5 w-5 items-center justify-center rounded-full border border-slate-900 bg-white text-slate-900 lg:h-7 lg:w-7">
                                        <i className="bi bi-check-lg text-[9px] lg:text-xs" aria-hidden />
                                    </span>
                                ) : null}
                                <span
                                    className={`inline-flex min-h-8 min-w-8 items-center justify-center rounded-full px-1.5 text-[11px] font-bold text-slate-800 lg:min-h-12 lg:min-w-12 lg:px-3 lg:text-base ${checked ? "bg-blue-100 ring-1 ring-blue-200" : "bg-blue-50"}`}
                                >
                                    {lang.badge}
                                </span>
                                <span className="hidden text-xs font-semibold text-slate-800 lg:inline">
                                    {lang.label}
                                </span>
                            </label>
                        );
                    })}
                </div>
                {touched.language && errors.language ? (
                    <p className="mt-2 text-xs text-red-600 md:text-sm">{errors.language}</p>
                ) : null}
            </section>
        </>
    );
}

const BasicDetails = ({ onValidityChange, readOnly = false }) => {
    const userId = useSelector((state) => state.auth.user?.uid);
    const stored = useSelector((state) => state.campaignBuilder.basicDetails);
    const defaults = useMemo(() => getCampaignBuilderDefaults(userId), [userId]);

    const initialValues = useMemo(
        () => ({
            name: stored.name ?? "",
            platform: stored.platform || defaults.basicDetails.platform,
            language: stored.language || defaults.basicDetails.language,
        }),
        [stored.name, stored.platform, stored.language, defaults]
    );

    return (
        <div className="col-md-12">
            <div className="w-full px-0 py-2 md:px-2 md:py-3">
                <h4 className="mb-4 flex items-center gap-2 text-xl font-bold text-slate-900 md:mb-6 md:gap-3 md:text-2xl">
                    <span className="inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-slate-100 text-slate-700 md:h-10 md:w-10">
                        <i className="bi bi-card-text text-lg md:text-xl" aria-hidden />
                    </span>
                    Set Basics
                </h4>

                <Formik
                    key={`${initialValues.platform}-${initialValues.language}`}
                    initialValues={initialValues}
                    validationSchema={validationSchema}
                    validateOnMount={!readOnly}
                    validateOnChange={!readOnly}
                    validateOnBlur={!readOnly}
                    enableReinitialize
                    onSubmit={() => {}}
                >
                    {(formikProps) => (
                        <BasicDetailsFields
                            {...formikProps}
                            readOnly={readOnly}
                            onValidityChange={onValidityChange}
                        />
                    )}
                </Formik>
            </div>
        </div>
    );
};

export default BasicDetails;
